import { ContactMatchingPhoneNumberResult } from "@/Common/Api/CallCenter/Responses/ContactMatchingPhoneNumberResult";
import { IndividualMatchingPhoneNumberComposite } from "@/Common/Api/CallCenter/Responses/IndividualMatchingPhoneNumberComposite";
import { LinkedContact } from "@/Common/Api/CallCenter/Responses/LinkedContact";
import { PatientRefIntervenorWithDetails } from "@/Common/Api/Patient/Dto/PatientRefIntervenorWithDetails";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { IndividualMatchingPhoneNumber } from "@/Common/Models/CallCenter/IndividualMatchingPhoneNumber";
import { Guid } from "@/Common/Models/Guid";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { PatientBannerUpcomingAppointment } from "../Appointment/PatientBannerUpcomingAppointment";
import { StatusComponent } from "../Patient/StatusComponent";
import { PortalStatusIcon } from "../Portal/PortalStatusIcon";

import styles from "./MatchedIndividualListItem.module.scss";

import blueStarImage from "@/Common/_images/Icons/Ramq/BlueStart.png";
import placeholderPatientImage from "@/Common/_images/patient-icon.png";
import placeholderRelativeImage from "@/Common/_images/relative-icon.png";

interface IMatchedIndividualListItemProps {
    ResultItem: ContactMatchingPhoneNumberResult;
    profilePictureVersion: Guid;
    getLinkedIndividual: (contactId: Guid) => IndividualMatchingPhoneNumberComposite;
    onItemClick: (contactMatching: ContactMatchingPhoneNumberResult) => Promise<void>;
}

function MatchedIndividualListItem(props: Readonly<IMatchedIndividualListItemProps>): JSX.Element {
    function getPatientInsurerDetails(patient: Patient): JSX.Element {
        const formattedCV = patient.GetFormattedRamqCV();
        const cvSplitter = IsNullOrWhiteSpace(formattedCV) ? "" : " | ";

        return (
            <div className={styles.Insurer}>
                <StatusComponent
                    status={patient.GetAssignedDoctorStatus()}
                    statusInfo={`${patient.GetFormattedRamqDoctorOrStatus()}${cvSplitter}${formattedCV}`}
                />
            </div>
        );
    }

    function getPatientPrimaryPhysicianDetails(patient: Patient): JSX.Element {
        const formattedPhys = patient.GetFormattedIntervenorOnFile();
        if (IsNullOrWhiteSpace(formattedPhys)) {
            return null;
        }

        return (
            <div className={styles.PrimaryPhys}>
                <img alt="star" className="margin-xs-left" style={{ width: "12px" }} src={blueStarImage} />
                <span className="margin-xs-left">
                    {formattedPhys}
                </span>
            </div>
        );
    }

    function getRelationshipLabels(linkedContacts: LinkedContact[]): JSX.Element {
        if (linkedContacts == null || linkedContacts.length == 0) {
            return null;
        }

        const relationItems = linkedContacts.map(link => {
            const repLabel = link.IsRepresentative ? " (" + cacheStore.GetString("RepresentativeLabel") + ")" : "";
            const typeLabel = cacheStore.GetLocalizedValueByLookupId(link.RelationLookupId);
            const linkedIndividualInterface = props.getLinkedIndividual(GuidHelper.AsGuid(link.OriginContactId));
            if (linkedIndividualInterface == null) {
                return null;
            }
            const individual = new IndividualMatchingPhoneNumber(linkedIndividualInterface);
            const ofLabel = cacheStore.GetString("OfLabel");
            const person = individual.ToPerson();
            const name = person.Name;
            return (<div key={link.Id} className={styles.RelationItem}>{typeLabel} {ofLabel} {name}{repLabel}</div>);
        });

        return (<>{relationItems}</>);
    }

    function getGenderIcon(gender: Gender): JSX.Element {
        let genderClassName = null;
        switch (gender) {
            case Gender.Male:
                genderClassName = "male";
                break;
            case Gender.Female:
                genderClassName = "female";
                break;
            default:
                return null;
        }

        return (<span className={`${styles.GenderIcon} myle-icon-16 ${genderClassName}`} />);
    }

    function getPronounsLabel(pronouns: string): JSX.Element {
        if (pronouns == null || pronouns == "") {
            return null;
        }

        return (<span className={styles.Pronouns}>{pronouns}</span>);
    }

    function getPatientDetails(contact: ContactMatchingPhoneNumberResult): { left: JSX.Element, right: JSX.Element } {
        const profileImage = contact.ProfilePicture != null
            ? apiSettings.buildAppUrl(`api/patient/profile/image/fetch/${contact.ProfilePicture.Id}?_nocache=${props.profilePictureVersion}`, { noCache: false, auth: true })
            : placeholderPatientImage;

        const registerPortalButtonProps = cacheStore.ClinicSettings_Portal.EnablePortalWebsite
            ? PortalHelper.getPortalButtonPropsForRegistrationState(contact.Individual.PortalSyncState, true)
            : null;

        const matchedIndividual = new IndividualMatchingPhoneNumber(contact.Individual);
        const patient = matchedIndividual.ToPatient();
        if (contact.RAMQRegistrationResponse != null) {
            patient.RAMQInfo = new RAMQInfo();
            patient.RAMQInfo.FromResponseData(contact.RAMQRegistrationResponse);
        }
        if (contact.PrimaryPatientRefIntervenors != null) {
            patient.IntervenorsOnFile = contact.PrimaryPatientRefIntervenors.map(physician => new PatientRefIntervenorWithDetailsModel(new PatientRefIntervenorWithDetails(physician)));
        }

        const name = patient.GetFormattedName();
        const pronounsLabel = getPronounsLabel(patient.GetFormattedPreferredPronouns());
        const genderIcon = getGenderIcon(patient.Gender());
        const relationships = getRelationshipLabels(contact.LinksToIndividuals);
        const dateOfBirthLabel = cacheStore.GetString("DateofBirthLabel");
        const dateOfBirth = patient.GetFormattedDateOfBirth();
        const age = patient.GetFormattedAge();
        const insurerDetails = getPatientInsurerDetails(patient);
        const physDetails = getPatientPrimaryPhysicianDetails(patient);
        const physInsurerSepartor = physDetails != null ? " / " : "";
        const nextAppointmentLabel = cacheStore.GetString("HeaderNextVisitLabel");

        return {
            left:
            (
                <>
                    <img alt="profile" src={profileImage} className={styles.ProfilePicture} />
                    <PortalStatusIcon {...registerPortalButtonProps} />
                </>
            ),
            right:
            (
                <>
                    <div className={styles.NameRow}>
                        <span className={styles.Name}>{name}</span>
                        {pronounsLabel}
                        {genderIcon}
                    </div>
                    {relationships}
                    <div className={styles.DateOfBirthRow}>
                        <span className={styles.DateOfBirthLabel}>{dateOfBirthLabel}</span>
                        <span>{dateOfBirth}</span>
                        <span>{age}</span>
                    </div>
                    <div className={styles.PhysInsurerRow}>
                        {physDetails}{physInsurerSepartor}{insurerDetails}
                    </div>

                    <div className={styles.AppointmentRow}><span className={styles.AppointmentWrapper}><PatientBannerUpcomingAppointment appointment={contact.NextUpcomingAppointment} label={nextAppointmentLabel} /></span></div>
                </>
            )
        };
    }

    function getRelativeDetails(contact: ContactMatchingPhoneNumberResult): { left: JSX.Element, right: JSX.Element } {
        const matchedIndividual = new IndividualMatchingPhoneNumber(contact.Individual);
        const person = matchedIndividual.ToPerson();
        const genderIcon = getGenderIcon(person.Gender());
        const pronounsLabel = getPronounsLabel(person.GetFormattedPreferredPronouns());
        const relationships = getRelationshipLabels(contact.LinksToIndividuals);

        return {
            left:
            (
                <img alt="profile" src={placeholderRelativeImage} className={styles.ProfilePicture} />
            ),
            right:
            (
                <>
                    <div className={styles.NameRow}>
                        <span className={styles.Name}>{person.Name}</span>
                        {pronounsLabel}
                        {genderIcon}
                    </div>
                    {relationships}
                </>
            )
        };
    }

    function getDetails(contact: ContactMatchingPhoneNumberResult): { left: JSX.Element, right: JSX.Element } {
        if (contact.Individual.ContactFacetType == ContactFacetType.Patient) {
            return getPatientDetails(contact);
        }
        else {
            return getRelativeDetails(contact);
        }
    }
    const { left, right } = getDetails(props.ResultItem);
    const canSelect = props.ResultItem.Individual.ContactFacetType == ContactFacetType.Patient;
    const itemClassName = `${styles.ListItem} ${canSelect && styles.Selectable}`;

    async function selectPatientForCallLog(): Promise<void> {
        if (!canSelect) {
            return;
        }

        const { ResultItem, onItemClick } = props;
        return onItemClick(ResultItem);
    }

    return (
        <div className={itemClassName} onClick={selectPatientForCallLog}>
            <div className={styles.LeftBar}>
                {left}
            </div>
            <div className={styles.RightBar}>
                {right}
            </div>
        </div>
    );
}

export { IMatchedIndividualListItemProps, MatchedIndividualListItem };
